import classNames from "classnames";
import { TFunction } from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import { Column } from "react-table";

import style from "./mcs.scss";
import McsKebabMenu from "./McsKebabMenu";
import { McsFilter } from "./McsQuickFilters";
import McSyncInformationView from "./McSyncInformationView";
import InfoWithBorder from "components/icons/InfoWithBorder";
import RedirectToView from "components/icons/RedirectToView";
import { createIbrLicenseTypes } from "components/licenses/common";
import { ENTITLEMENT_STATE_MAPPING } from "components/licenses/entitlements/EntitlementDetailsModal";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import EntitlementManualActivation from "components/mcs/EntitlementManualActivation";
import Modal from "components/modal/Modal";
import { ENTITLEMENT_LICENSES_ROUTE } from "components/router/Routes";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import DateCell from "components/table/DateCell";
import Table, { deriveColumnWidth } from "components/table/Table";
import TextWithTooltip from "components/table/TextWithTooltip";
import { TextBlock } from "components/typography/textBlock/TextBlock";
import { AUTH_EMS_CREATE_ENTITLEMENT } from "domain/authority";
import { SUPPORT_EMAIL, TABLE_PAGE_LIMIT } from "domain/globalConstants";
import {
    Container,
    EmsConfiguration,
    EntitlementState,
    getEntitlementStateDisplay,
    getEntitlementStateInfo,
    LicenseData,
} from "domain/licenses";
import { McTable } from "domain/mc";
import { getMcStatusDisplay, Mcs, McsTableData, McStatus } from "domain/mc";
import { hybridMcService } from "services/hybrid-mc/HybridMcService";
import { hasEmsId } from "services/licenses/licenseRepository";
import { ProductRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { userSessionService } from "services/user/UserSessionService";
import buttons from "styles/buttons.scss";
import layoutStyle from "styles/layout.scss";
import { RepositoryKey } from "utils/repository";
import { useTheme } from "utils/useTheme";

import infoBadge from "assets/images/icons/product-icons/infoBadge.svg";

import testIds from "testIds.json";

interface TableState {
    Mcs: McsTableData[];
    cursor: string;
    desired: number;
    scrollPosition?: number;
}

export interface Props {
    count: number;
    refreshCount: number;
    initialMcsData: Mcs;
    search?: string;
    onMcChange: () => void;
    licenseDeliveryCheck: boolean;
    licenses: LicenseData[];
    emsConfiguration: EmsConfiguration;
    allContainers: Container[];
    tokenRates?: ProductRateList[];
    autoOpenCreateEntitlement?: boolean;
    activeFilter: McsFilter;
    onDataFetch?: (data: McTable) => void;
    isLoadingEmsData?: boolean;
}

const STATUS_MAPPING: Record<McStatus, Status> = {
    [McStatus.ACTIVE]: Status.SUCCESS,
    [McStatus.UNREGISTERED]: Status.NEUTRAL,
    [McStatus.DISABLED]: Status.ERROR,
};

export function calculateDaysUntilHalt(
    now: Date,
    mc: McsTableData,
    desiredSeconds: number
): { desiredPassed: boolean; days: number } {
    const desiredMillis = desiredSeconds * 1000;
    const theoreticalDesiredMillis = new Date(mc.lastUpdatedDate).getTime() + desiredMillis;
    const desiredPassed = theoreticalDesiredMillis < now.getTime();
    const deadlineDate = new Date(mc.deadlineDate);
    return {
        desiredPassed,
        days: desiredPassed ? Math.ceil((deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 0,
    };
}

export const deriveEntitlementState = (
    t: TFunction,
    value: EntitlementState,
    updationDate: string | undefined,
    inDialog: boolean
) => {
    const status: Status = ENTITLEMENT_STATE_MAPPING[value];
    const translationKey = inDialog
        ? getEntitlementStateInfo(t, value, updationDate)
        : getEntitlementStateDisplay(t, value, updationDate);
    return {
        status: status,
        title: translationKey,
    };
};

const McsTable = (props: Props): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [tableState, setTableState] = React.useState<TableState>({
        Mcs: [],
        cursor: "",
        desired: 0,
        scrollPosition: 0,
    });
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string | JSX.Element>("");
    const [initialLoading, setInitialLoading] = React.useState<boolean>(false);
    const [mcSyncInformationVisibility, setMcSyncInformationVisibility] = React.useState(false);
    const [selectedMc, setSelectedMc] = React.useState<McsTableData>();
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const rowRefs = React.useRef<Array<React.RefObject<HTMLTableRowElement>>>([]);
    const deactivatedStatusContent = { status: Status.ERROR, title: t("Mcs.table.mcStatus.deactivated") };
    const haltedStatusContent = { status: Status.ERROR, title: t("Mcs.table.mcStatus.disabledByBlanccoText") };
    const mailToLink = `mailto:${SUPPORT_EMAIL}`;
    const [entitlementManualActivation, setEntitlementManualActivation] = React.useState<boolean>(false);
    const [selectedEntitlement, setSelectedEntitlement] = React.useState<{
        entitlementId?: string;
        entitlementState?: EntitlementState;
    }>();
    const hideSyncInformation = () => {
        setMcSyncInformationVisibility(false);
    };

    function showManualEntitlementActivationDialog() {
        if (
            !entitlementManualActivation ||
            !selectedEntitlement ||
            !selectedEntitlement.entitlementId ||
            !selectedEntitlement.entitlementState
        ) {
            return null;
        }
        return (
            <EntitlementManualActivation
                visibility={entitlementManualActivation}
                onMarkAsActivated={props.onMcChange}
                setVisibility={setEntitlementManualActivation}
                entitlementState={selectedEntitlement.entitlementState}
                entitlementId={selectedEntitlement.entitlementId}
            />
        );
    }

    const viewSyncInformation = (
        <Modal
            isOpen={mcSyncInformationVisibility}
            hideModal={hideSyncInformation}
            modalTitle={t("Mcs.onPremBmpInfoModal.title")}
        >
            {selectedMc != null && <McSyncInformationView mcsTableData={selectedMc} onClick={hideSyncInformation} />}
        </Modal>
    );

    const errorText = (
        <div>
            <div className={style.errorText}>
                <span>{t("Mcs.table.requestFailed")}</span>
                <span>{t("Mcs.table.requestFailed1")}</span>
                <span>{t("Mcs.table.requestFailed2")}</span>
            </div>
        </div>
    );

    const licenseDeliveryMissingErrorText = (
        <div>
            <div className={style.errorText}>
                <span>{t("Mcs.table.requestFailed3")}</span>
                <TextBlock alignCenter={true}>
                    <a href={mailToLink}>{t("Common.genericContactSupport2")}</a> {t("Mcs.table.requestFailed4")}
                </TextBlock>
            </div>
        </div>
    );

    React.useEffect(() => {
        if (props.licenseDeliveryCheck && !initialLoading) {
            setRequestFailureMessage(licenseDeliveryMissingErrorText);
        } else {
            setRequestFailureMessage("");
        }
    }, [props.licenseDeliveryCheck, initialLoading]);

    const fetchData = (initialLoading: boolean) => {
        setLoading(true);
        setInitialLoading(initialLoading);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const params = initialLoading
            ? { filter: props.activeFilter }
            : { cursor: tableState.cursor, filter: props.activeFilter };
        hybridMcService
            .fetchData(abortController, params)
            .then((data) => {
                setTableState((prevState) => ({
                    ...prevState,
                    scrollPosition: prevState.Mcs.length - 1,
                    Mcs: initialLoading ? data.mcdata : prevState.Mcs.concat(data.mcdata),
                    desired: data.desired,
                    cursor: data.cursor,
                }));
                setLoading(false);
                props.onDataFetch?.(data);
            })
            .catch(() => {
                if (!abortController?.signal.aborted) {
                    setRequestFailureMessage(errorText);
                }
            })
            .finally(() => {
                if (!abortController?.signal.aborted) {
                    setLoading(false);
                    setInitialLoading(false);
                }
            });
    };
    const deriveStatusConfig = (value: McStatus) => {
        const status: Status = STATUS_MAPPING[value];
        const translationKey = getMcStatusDisplay(value);
        return {
            status: status,
            title: t(translationKey),
        };
    };

    const deriveStatus = (desired: number, mc: McsTableData) => {
        const { desiredPassed, days } = calculateDaysUntilHalt(new Date(), mc, desired);
        if (desiredPassed) {
            if (days > 0) {
                return (
                    <div>
                        <span className={classNames(style.statusBadge, style.statusWarning)}>
                            {t("Mcs.table.mcStatus.syncText", { days })}
                        </span>
                    </div>
                );
            } else {
                return (
                    <div>
                        <span className={classNames(style.statusBadge)}>
                            <StatusBadge values={[deactivatedStatusContent]} tooltip={true} />
                        </span>
                    </div>
                );
            }
        } else {
            return (
                <div>
                    <span className={classNames(style.statusBadge)}>
                        <StatusBadge values={[deriveStatusConfig(mc.status)]} tooltip={true} />
                    </span>
                </div>
            );
        }
    };

    const isBottomElement = (scrollRef: React.RefObject<HTMLTableRowElement>): boolean => {
        if (!scrollRef.current || !tableContainerRef.current) {
            return false;
        }
        const containerBounds = tableContainerRef.current.getBoundingClientRect();
        const rowBounds = scrollRef.current.getBoundingClientRect();
        const containerTop = containerBounds.top;
        const rowPositionInContainer = rowBounds.top - containerTop;
        const bottomBoundary = containerBounds.height * 0.66;

        return rowPositionInContainer > bottomBoundary;
    };

    function deducePreselectedLicenses(mcLicenses: string[]): string[] {
        const preselectedLicenses: string[] = [];
        const legacyWorkflowIds = createIbrLicenseTypes();
        mcLicenses.forEach((each) => {
            const legacyLicense = legacyWorkflowIds.find((eachLegacyId) => eachLegacyId.productId === each);
            if (legacyLicense) {
                preselectedLicenses.push(legacyLicense.productName);
            } else {
                preselectedLicenses.push(each);
            }
        });
        return preselectedLicenses;
    }

    const columns: Array<Column<McsTableData>> = [
        {
            Header: () => <TextWithTooltip text={t("ApiKeys.name")} key="1" />,
            accessor: "name",
            Cell: (cellInfo) => {
                const rowRef = React.useRef<HTMLTableRowElement>(null);
                rowRefs.current.push(rowRef);
                return (
                    <div ref={rowRef} className={classNames(style.alignment)}>
                        <div className={classNames(style.marginRight)}>
                            {!props.isLoadingEmsData && (
                                <McsKebabMenu
                                    uuid={cellInfo.cell.row.original.uuid}
                                    name={cellInfo.cell.row.original.name}
                                    description={cellInfo.cell.row.original.description}
                                    status={cellInfo.cell.row.original.status}
                                    onMcChange={props.onMcChange}
                                    isBottomElement={isBottomElement(rowRef)}
                                    tableState={[]}
                                    availableLicenses={props.licenses}
                                    emsConfiguration={props.emsConfiguration}
                                    allContainers={props.allContainers}
                                    mcVersion={cellInfo.cell.row.original.mcVersion}
                                    mcContainer={cellInfo.cell.row.original.containerId}
                                    entitlementState={cellInfo.cell.row.original.entitlementState}
                                    tokenRates={props.tokenRates}
                                    isLoadingEmsData={props.isLoadingEmsData}
                                    preselectedProducts={
                                        cellInfo.cell.row.original.licenses
                                            ? deducePreselectedLicenses(
                                                  Object.keys(cellInfo.cell.row.original.licenses)
                                              )
                                            : []
                                    }
                                    autoOpenCreateEntitlement={props.autoOpenCreateEntitlement}
                                    totalMcsCount={tableState.Mcs.length}
                                />
                            )}
                        </div>
                        <a
                            onClick={() => {
                                setMcSyncInformationVisibility(true);
                                setSelectedMc(cellInfo.cell.row.original);
                            }}
                        >
                            <TextWithTooltip text={cellInfo.value} />
                        </a>
                    </div>
                );
            },
        },
        {
            Header: () => <TextWithTooltip text={t("Mcs.table.description")} key="2" />,
            accessor: "description",
            Cell: ({ cell: { value } }) => <TextWithTooltip text={value} />,
            width: deriveColumnWidth(10, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("Mcs.table.registrationDate")} key="3" />,
            accessor: "registrationDate",
            Cell: ({ cell: { value } }) => <DateCell tooltip={true} value={value} withoutTime={true} />,
            width: deriveColumnWidth(18, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("Mcs.table.entitlementState")} key="4" />,
            accessor: "entitlementState",
            Cell: ({ cell: { value, row } }) => {
                if (value != null) {
                    return (
                        <div className={style.entitlementActionsRow}>
                            <div className={style.leftSection}>
                                <div className={style.statusBadge}>
                                    <StatusBadge
                                        values={[
                                            deriveEntitlementState(t, value, row.original.entitlementUpdateDate, false),
                                        ]}
                                        tooltip={true}
                                    />
                                </div>

                                <button
                                    className={classNames(buttons.textButton, style.manualActivationButton)}
                                    hidden={
                                        !userSessionService.userHasAnyAuthority([AUTH_EMS_CREATE_ENTITLEMENT]) ||
                                        row.original.entitlementState === EntitlementState.ACTIVATED ||
                                        row.original.entitlementState === EntitlementState.MANUALLY_ACTIVATED
                                    }
                                    onClick={() => {
                                        setSelectedEntitlement({
                                            entitlementId: row.original.entitlementId,
                                            entitlementState: row.original.entitlementState,
                                        });
                                        setEntitlementManualActivation(true);
                                    }}
                                    data-testid={testIds.workArea.mcs.table.entitlementManualActivationButton}
                                >
                                    <InfoWithBorder
                                        color={theme.contentBorderColor}
                                        borderColor={theme.iconFillColor}
                                    />
                                    {t("Mcs.table.entitlementManualActivation")}
                                </button>
                                {hasEmsId() && (
                                    <button
                                        className={style.entitlementViewButton}
                                        onClick={() => window.location.replace(ENTITLEMENT_LICENSES_ROUTE.path)}
                                    >
                                        <RedirectToView color={theme.iconFillColor} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                } else {
                    return null;
                }
            },
            width: deriveColumnWidth(24, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("Mcs.table.lastSynced")} key="5" />,
            accessor: "lastUpdatedDate",
            Cell: ({ cell: { value } }) => <DateCell tooltip={true} value={value} withoutTime={false} />,
            width: deriveColumnWidth(18, tableContainerRef),
        },
        {
            Header: () => (
                <div className={classNames(style.statusTitleInfo)} key="6">
                    <TextWithTooltip text={t("Mcs.table.status")} />
                    <TextWithTooltip text={t("Mcs.table.statusInfo")}>
                        <img src={infoBadge} />
                    </TextWithTooltip>
                </div>
            ),
            accessor: "status",
            Cell: (cellInfo) => {
                const mc = cellInfo.cell.row.original;

                if (mc.status == McStatus.UNREGISTERED || mc.status == McStatus.DISABLED) {
                    return (
                        <div>
                            <span className={classNames(style.statusBadge)}>
                                <StatusBadge values={[deriveStatusConfig(mc.status)]} tooltip={true} />
                            </span>
                        </div>
                    );
                }

                if (mc.halted) {
                    return (
                        <div>
                            <span className={classNames(style.statusBadge)}>
                                <StatusBadge values={[haltedStatusContent]} tooltip={true} />
                            </span>
                        </div>
                    );
                }

                const statusResult = deriveStatus(tableState.desired, mc);
                return (
                    <div>
                        <span className={classNames(style.statusBadge)}>{statusResult}</span>
                    </div>
                );
            },
            width: deriveColumnWidth(15, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("Mcs.table.uuid")} key="7" />,
            accessor: "uuid",
            Cell: ({ cell: { value } }) => <TextWithTooltip text={value} />,
            width: deriveColumnWidth(25, tableContainerRef),
        },
        {
            Header: () => <TextWithTooltip text={t("Mcs.table.mcVersion")} key="8" />,
            accessor: "mcVersion",
            Cell: ({ cell: { value } }) => <TextWithTooltip text={value} />,
            width: deriveColumnWidth(10, tableContainerRef),
        },
    ];

    React.useEffect(() => {
        setTableState({ Mcs: [], cursor: "", desired: 0, scrollPosition: 0 });
        fetchData(true);
    }, [props.count, props.refreshCount, props.activeFilter]);

    const filteredData = tableState.Mcs;
    //filterData(tableState.Mcs, props.activeFilter);
    let dataCount = null;
    if (filteredData.length > 0) {
        dataCount = <div>{t("Common.defaultSearchResultHint", { dataCount: tableState.Mcs.length })}</div>;
    }

    return (
        <>
            <div className={layoutStyle.aboveTable}>
                <div className={layoutStyle.recordCount}>{dataCount}</div>
            </div>
            <div className={layoutStyle.tableWrapper} ref={tableContainerRef}>
                <Table
                    tableIdentity={RepositoryKey.MCS_TABLE}
                    data={filteredData}
                    columns={columns}
                    loaded={!initialLoading && !props.isLoadingEmsData}
                    failureMessage={requestFailureMessage}
                    tooltips={true}
                    scrollTo={tableState.scrollPosition}
                    emptyMessage={""}
                    testId={testIds.workArea.mcs.table.itself}
                />
            </div>

            {tableState.cursor != null &&
                tableState.Mcs.length >= TABLE_PAGE_LIMIT &&
                tableState.Mcs.length != 0 &&
                requestFailureMessage === "" &&
                (loading ? (
                    <LoadingIndicator small={true} withTable />
                ) : (
                    <button
                        className={buttons.loadMoreButtonWithoutIcon}
                        onClick={() => {
                            fetchData(false);
                            usageStatisticsService.sendEvent({
                                action: Action.LOAD_MORE,
                                category: Category.MCS,
                            });
                        }}
                        data-testid={testIds.common.primaryView.table.loadMoreButton}
                    >
                        {t("Common.loadMore")}
                    </button>
                ))}
            {viewSyncInformation}
            {showManualEntitlementActivationDialog()}
        </>
    );
};

export default McsTable;
